'use client'
import {
  FormilyInput,
  FormilySelect,
  FormilyFormItem,
  FormilyCheckbox,
  FormilySwitch,
  FormilyRadio,
  FormilySlider,
  FormilyTextarea,
  FormilyInputNumber,
  FormilySelectV2,
} from '@/components/formily'
import { useDynamicI18n } from '@/hooks/use-dynamic-i18n'
import { createForm } from '@formily/core'
import type { Form } from '@formily/core'
import { createSchemaField, FormProvider } from '@formily/react'
import { useMemo, forwardRef, useImperativeHandle, useEffect } from 'react'
import { get, set, cloneDeep } from 'lodash'
import { FormilyRichText } from './rich-text'
import { FormilyDateTimePicker } from './date-time-picker'
import { FormilyUploader } from './uploader'
import { FormilyCreditAlert } from './credit-alert'

const SchemaField = createSchemaField({
  components: {
    Input: FormilyInput,
    InputNumber: FormilyInputNumber,
    Uploader: FormilyUploader,
    Select: FormilySelect,
    SelectV2: FormilySelectV2,
    FormItem: FormilyFormItem,
    Checkbox: FormilyCheckbox,
    Switch: FormilySwitch,
    Radio: FormilyRadio,
    Slider: FormilySlider,
    Textarea: FormilyTextarea,
    RichText: FormilyRichText,
    DateTimePicker: FormilyDateTimePicker,
    CreditAlert: FormilyCreditAlert,
  },
})

interface IFormilyRendererProps {
  formilySchema: any
  defaultValues?: Record<string, any>
  previewMode?: boolean
  needTranslation?: boolean
  // 只渲染 ui 组件
  onlyRenderUiNodes?: boolean
}

export interface IFormilyRendererRef {
  form: Form
}

export const FormilySchemaRenderer = forwardRef<
  IFormilyRendererRef,
  IFormilyRendererProps
>(
  (
    {
      formilySchema,
      defaultValues,
      previewMode = false,
      needTranslation = false,
      onlyRenderUiNodes = false,
    },
    ref,
  ) => {
    const { dt } = useDynamicI18n({
      previewMode,
    })

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const form = useMemo(
      () =>
        createForm({
          validateFirst: false,
          initialValues: defaultValues,
        }),
      [formilySchema, defaultValues],
    )

    const translatedSchema = useMemo(() => {
      if (!formilySchema || !needTranslation) return formilySchema
      if (Object.keys(formilySchema || {}).length === 0) return formilySchema

      const schema = cloneDeep(formilySchema)

      // 需要翻译的 keys
      const needTranslateKeys = [
        'description',
        'title',
        'x-component-props.tooltip',
        'x-component-props.placeholder',
      ]

      // 翻译函数
      const translate = (value: string) => {
        if (typeof value === 'string' && !value.startsWith('{{dt')) {
          return `{{dt("${value}")}}`
        }
        return value
      }

      // 现在的结构比较简单，之后可能需要换成递归
      for (const key of Object.keys(schema.properties)) {
        const property = schema.properties[key]

        for (const needTranslateKey of needTranslateKeys) {
          const value = get(property, needTranslateKey)
          if (value) {
            set(property, needTranslateKey, translate(value))
          }
        }
      }

      if (onlyRenderUiNodes) {
        for (const key of Object.keys(schema.properties)) {
          const property = schema.properties[key]
          if (property.type !== 'void') {
            delete schema.properties[key]
          }
        }
      }

      return schema
    }, [formilySchema, needTranslation, onlyRenderUiNodes])

    useImperativeHandle(ref, () => ({
      form,
    }))

    return (
      <div className="space-y-4 w-full">
        <FormProvider form={form}>
          <SchemaField schema={translatedSchema} scope={{ dt }} />
        </FormProvider>
      </div>
    )
  },
)
